import {ChangeDetectionStrategy, Component, effect, HostListener, input, Input, ViewChild} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatDatepickerModule, MatDateRangePicker} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import { DateRangeFilterService } from '@nagel-services';
import { getEndOfDay, getIsInvalidDate } from '@nagel-utils';
import { DateRangeFormType } from 'libs/nagel-types/src/lib/field-types';

@Component({
    selector: 'single-date-range-picker',
    templateUrl: 'single-date-range-picker.component.html',
    styleUrl: 'single-date-range-picker.component.scss',
    providers: [DateRangeFilterService],
    imports: [
        MatFormFieldModule,
        MatDatepickerModule,
        FormsModule,
        ReactiveFormsModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SingleDateRangePickerComponent {
    @Input() storageKey: string = '';
    value = input<DateRangeFormType | null>(null);
    @Input() hideEmptyValues: boolean = false;
    @ViewChild('picker') picker!: MatDateRangePicker<Date>;
    @Input() handleOnSubmit?: (formValue: DateRangeFormType | null) => void = () => {};
    @Input() isMenuElement: boolean = false;

    range = new FormGroup({
        startDate: new FormControl<Date | null>(null),
        endDate: new FormControl<Date | null>(null)
    });

    constructor(
        private dateRangeFilterService: DateRangeFilterService
    ) {
        effect(() => {
            if (this.value()) {
                this.dateRangeFilterService.handleOnInit(this.storageKey, this.isMenuElement, this.value());
            }
        });
    }

    ngOnInit(): void {
        this.dateRangeFilterService.setForm(this.range);

        this.dateRangeFilterService.handleOnInit(this.storageKey, this.isMenuElement, this.value());

        const serviceForm = this.dateRangeFilterService.getForm();

        if (this.value() || serviceForm.value.startDate) {
            const dateRange = this.buildDateRange();
            if (dateRange && (!this.isMenuElement || this.value())) {
                this.handleOnSubmit?.(dateRange as DateRangeFormType);
            }
        }
    }

    ngAfterViewInit() {
        this.picker.closedStream.subscribe(() => {
            if (!this.isMenuElement) {
                this.dateRangeFilterService.patchFormValues(this.range.value as DateRangeFormType);
            }
        });
    }

    isDatePickerDisabled() {
        return false;
    }

    shouldDisableCancelButton = () => {
        //Make a check if there are changes in the form
        return false;
    };

    onSubmit = (event: Event) => {
        event.preventDefault();
        const dateRange = this.buildDateRange();
        this.dateRangeFilterService.patchFormValues(dateRange as DateRangeFormType);

        this.handleOnSubmit && this.handleOnSubmit(dateRange as DateRangeFormType);
    };

    onCancel = (event: Event) => {
        event.preventDefault();

        this.range.reset(this.dateRangeFilterService.currentDateRangeSubject.value);
    };

    ngOnDestroy(): void {
        if (!this.isMenuElement) {
            this.dateRangeFilterService.handleOnDestroy(this.storageKey);
        }
    }

    @HostListener('window:beforeunload')
        handleOnPageRefresh = () => {
            if (!this.isMenuElement) {
                this.dateRangeFilterService.handleOnDestroy(this.storageKey);
            }
        };

    private buildDateRange() {
        const dateRange = {...this.range.value};

        if (getIsInvalidDate(dateRange.startDate)) {
            return null;
        }

        if (!dateRange.endDate || getIsInvalidDate(dateRange.endDate)) {
            dateRange.endDate = dateRange.startDate;
        }

        const { startDate, endDate } = dateRange;

        if (startDate && endDate && startDate.toISOString() === endDate.toISOString()) {
            const newEndDate = getEndOfDay(endDate);
            dateRange.endDate = newEndDate;
        }

        return dateRange;
    }
}